<template>
  <div class="chip-label-input">
    <div
      class="chip-input-group"
      :class="{ focused: isFocused, dragging: draggingChipId !== null }">
      <div class="chip-input-segments">
        <template v-for="(seg, i) in segments">
          <span
            v-if="seg.type === 'text'"
            :key="seg.id"
            class="segment-text-wrap"
            :class="{ 'first-segment': i === 0 && segments.length === 1 }"
            :style="segmentWrapStyle(seg, i)">
            <input
              :ref="'seg-' + seg.id"
              :value="seg.value"
              class="segment-text"
              type="text"
              :placeholder="i === 0 && segments.length === 1 ? placeholder : ''"
              @input="onSegmentInput(seg, $event)"
              @focus="onSegmentFocus(seg, $event)"
              @blur="onSegmentBlur"
              @keydown="onSegmentKeydown(seg, i, $event)"
              @paste="onSegmentPaste(seg, i, $event)" />
          </span>
          <span
            v-else
            :key="seg.id"
            class="chip-tag"
            :class="{ 'no-drag': !isDraggable, 'is-dragging': seg.id === draggingChipId }"
            @pointerdown="isDraggable && onChipPointerdown(seg, $event)">
            <span
              v-if="isDraggable"
              class="chip-drag-icon">⠿</span>
            <span class="chip-label-text">{{ getChipLabel(seg) }}</span>
            <button
              type="button"
              class="remove-chip-btn"
              aria-label="Remove chip"
              @mousedown.prevent
              @click="removeChip(i)">×</button>
          </span>
        </template>
      </div>
    </div>
    <div class="chip-controls-row">
      <template v-if="showAddButton">
        <b-button
          v-for="ct in resolvedChipTypes"
          :key="ct.placeholder"
          type="is-light"
          size="is-small"
          icon-left="plus"
          class="ml-0"
          @click="addChip(ct)">
          {{ ct.label }}
        </b-button>
      </template>
      <b-tooltip
        v-if="chipTooltip"
        :label="chipTooltip"
        position="is-right"
        multilined
        size="is-medium"
        class="info-tooltip">
        <i class="material-icons info-icon">info</i>
      </b-tooltip>
    </div>
  </div>
</template>

<script>
  let nextSegId = 1

  function makeTextSeg (value = '') {
    const id = 'seg-' + nextSegId++
    return { type: 'text', value, id, }
  }

  function makeChipSeg (placeholder = '') {
    return { type: 'chip', placeholder, id: 'seg-' + nextSegId++, }
  }

  export default {
    name: 'ChipLabelInput',
    props: {
      value: {
        type: String,
        default: '',
      },
      chipPlaceholder: {
        type: String,
        default: '',
      },
      chipLabel: {
        type: String,
        default: 'Label',
      },
      chipTooltip: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      showAddButton: {
        type: Boolean,
        default: true,
      },
      chipTypes: {
        type: Array,
        default: null,
      },
    },
    data () {
      return {
        segments: [makeTextSeg(''),],
        lastFocusedSegId: null,
        lastCursorPos: 0,
        isFocused: false,
        measureCanvas: null,
        draggingChipId: null,
      }
    },
    computed: {
      resolvedChipTypes () {
        if (this.chipTypes && this.chipTypes.length) return this.chipTypes
        if (this.chipPlaceholder) return [{ placeholder: this.chipPlaceholder, label: this.chipLabel, },]
        return []
      },
      isDraggable () {
        const chipCount = this.segments.filter(s => s.type === 'chip').length
        const hasText = this.segments.some(s => s.type === 'text' && s.value.length > 0)
        return hasText || chipCount > 1
      },
    },
    watch: {
      value: {
        immediate: true,
        handler (newVal) {
          if (this.serialize() !== newVal) {
            this.segments = this.parse(newVal || '')
          }
        },
      },
      resolvedChipTypes () {
        this.segments = this.parse(this.value || '')
      },
    },
    mounted () {
      this.measureCanvas = document.createElement('canvas')
    },
    methods: {
      parse (str) {
        if (!str) return [makeTextSeg(''),]

        const types = this.resolvedChipTypes
        const parts = []
        let remaining = str

        while (remaining.length > 0) {
          let earliest = -1
          let earliestType = null
          for (const ct of types) {
            const idx = remaining.indexOf(ct.placeholder)
            if (idx !== -1 && (earliest === -1 || idx < earliest)) {
              earliest = idx
              earliestType = ct
            }
          }
          if (earliest === -1) {
            parts.push(makeTextSeg(remaining))
            remaining = ''
          } else {
            if (earliest > 0) parts.push(makeTextSeg(remaining.slice(0, earliest)))
            parts.push(makeChipSeg(earliestType.placeholder))
            remaining = remaining.slice(earliest + earliestType.placeholder.length)
          }
        }

        if (parts.length === 0 || parts[parts.length - 1].type !== 'text') {
          parts.push(makeTextSeg(''))
        }

        return this.normalizeSegments(parts)
      },

      serialize () {
        return this.segments.map(seg => {
          if (seg.type === 'chip') return seg.placeholder
          return seg.value
        }).join('')
      },

      normalizeSegments (segs) {
        if (!segs || segs.length === 0) return [makeTextSeg(''),]

        const result = []
        for (const seg of segs) {
          if (seg.type === 'text') {
            if (result.length > 0 && result[result.length - 1].type === 'text') {
              result[result.length - 1].value += seg.value
            } else {
              result.push(seg)
            }
          } else {
            if (result.length > 0 && result[result.length - 1].type === 'chip') {
              result.push(makeTextSeg(''))
            }
            if (result.length === 0) {
              result.push(makeTextSeg(''))
            }
            result.push(seg)
          }
        }

        if (result[result.length - 1].type !== 'text') {
          result.push(makeTextSeg(''))
        }

        return result
      },

      emitValue () {
        this.$emit('input', this.serialize())
      },

      onSegmentInput (seg, event) {
        if (this.draggingChipId !== null) return
        seg.value = event.target.value
        this.emitValue()
        this.$nextTick(() => this._scrollInputIntoView(event.target))
      },

      onSegmentFocus (seg, event) {
        this.lastFocusedSegId = seg.id
        this.lastCursorPos = event.target.selectionStart || 0
        this.isFocused = true
      },

      onSegmentBlur () {
        setTimeout(() => {
          const active = document.activeElement
          if (!this.$el.contains(active)) {
            this.isFocused = false
          }
        }, 50)
      },

      onSegmentKeydown (seg, segIndex, event) {
        if (seg.type !== 'text' || this.draggingChipId !== null) return

        const input = event.target
        const pos = input.selectionStart
        const selEnd = input.selectionEnd

        if (event.key === 'Backspace' && pos === 0 && pos === selEnd && segIndex > 0) {
          const prev = this.segments[segIndex - 1]
          if (prev && prev.type === 'chip') {
            event.preventDefault()
            this.removeChip(segIndex - 1)
            return
          }
        }

        if (event.key === 'Delete' && pos === seg.value.length && pos === selEnd) {
          const next = this.segments[segIndex + 1]
          if (next && next.type === 'chip') {
            event.preventDefault()
            this.removeChip(segIndex + 1)
            return
          }
        }

        if (event.key === 'ArrowLeft' && pos === 0 && pos === selEnd && segIndex >= 2) {
          const prevChip = this.segments[segIndex - 1]
          const prevText = this.segments[segIndex - 2]
          if (prevChip && prevChip.type === 'chip' && prevText && prevText.type === 'text') {
            event.preventDefault()
            this.focusSegment(prevText.id, prevText.value.length)
            return
          }
        }

        if (event.key === 'ArrowRight' && pos === seg.value.length && pos === selEnd) {
          const nextChip = this.segments[segIndex + 1]
          const nextText = this.segments[segIndex + 2]
          if (nextChip && nextChip.type === 'chip' && nextText && nextText.type === 'text') {
            event.preventDefault()
            this.focusSegment(nextText.id, 0)
            return
          }
        }

        this.$nextTick(() => {
          this.lastCursorPos = input.selectionStart || 0
        })
      },

      onSegmentPaste (seg, segIndex, event) {
        if (this.draggingChipId !== null) return
        const pasted = (event.clipboardData || window.clipboardData).getData('text')
        if (!pasted.includes(this.chipPlaceholder)) return

        event.preventDefault()

        const input = event.target
        const pos = input.selectionStart || 0
        const end = input.selectionEnd || pos

        const newText = seg.value.slice(0, pos) + pasted + seg.value.slice(end)
        const newSegs = this.parse(newText)

        this.segments.splice(segIndex, 1, ...newSegs)
        this.segments = this.normalizeSegments(this.segments)
        this.emitValue()

        this.$nextTick(() => {
          const lastTextSeg = [...this.segments,].reverse().find(s => s.type === 'text')
          if (lastTextSeg) {
            this.focusSegment(lastTextSeg.id)
          }
        })
      },

      // --- Pointer-capture slide reorder ---

      onChipPointerdown (chipSeg, event) {
        if (event.target.closest('.remove-chip-btn')) return

        const chipEl = event.target.closest('.chip-tag')
        const container = this._getScrollContainer()
        container.setPointerCapture(event.pointerId)
        event.preventDefault()

        this.draggingChipId = chipSeg.id
        this._dragChipEl = chipEl
        this._dragContainer = container
        this._dragStartMouseX = event.clientX
        this._lastMouseX = event.clientX
        this._dragStartScrollLeft = container ? container.scrollLeft : 0

        // All chips render the same label, so measure this one for crossing width
        const cs = window.getComputedStyle(chipEl)
        this._otherChipWidth = chipEl.offsetWidth +
          (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0)

        this._recalcDragCharWidths()

        this._onPointermove = this.onChipPointermove.bind(this)
        this._onLostCapture = this.onChipLostCapture.bind(this)
        container.addEventListener('pointermove', this._onPointermove)
        container.addEventListener('lostpointercapture', this._onLostCapture)
      },

      _recalcDragCharWidths () {
        const chipIndex = this.segments.findIndex(s => s.id === this.draggingChipId)
        if (chipIndex === -1) return

        const ctx = this.measureCanvas.getContext('2d')
        ctx.font = '15px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

        const leftSeg = chipIndex > 0 ? this.segments[chipIndex - 1] : null
        const rightSeg = chipIndex < this.segments.length - 1 ? this.segments[chipIndex + 1] : null

        if (rightSeg && rightSeg.type === 'text' && rightSeg.value.length > 0) {
          const full = ctx.measureText(rightSeg.value).width
          const rest = ctx.measureText(rightSeg.value.slice(1)).width
          this._rightFirstCharWidth = full - rest
        } else {
          this._rightFirstCharWidth = 0
        }

        if (leftSeg && leftSeg.type === 'text' && leftSeg.value.length > 0) {
          const full = ctx.measureText(leftSeg.value).width
          const rest = ctx.measureText(leftSeg.value.slice(0, -1)).width
          this._leftLastCharWidth = full - rest
        } else {
          this._leftLastCharWidth = 0
        }
      },

      onChipPointermove (event) {
        if (this.draggingChipId === null) return
        this._lastMouseX = event.clientX
        this._updateDragPosition(event.clientX)
        this._checkDragAutoScroll()
      },

      _updateDragPosition (mouseX) {
        if (this.draggingChipId === null) return

        const container = this._getScrollContainer()
        const scrollDelta = container ? container.scrollLeft - this._dragStartScrollLeft : 0
        let dx = mouseX - this._dragStartMouseX + scrollDelta

        // Slide right: consume characters, then cross chips
        while (dx > 0) {
          const ci = this.segments.findIndex(s => s.id === this.draggingChipId)
          if (ci === -1) return
          const rightSeg = this.segments[ci + 1]

          // Consume a character from the right text segment
          if (rightSeg && rightSeg.type === 'text' && rightSeg.value.length > 0) {
            this._recalcDragCharWidths()
            if (this._rightFirstCharWidth > 0 && dx >= this._rightFirstCharWidth) {
              const leftSeg = this.segments[ci - 1]
              if (leftSeg && leftSeg.type === 'text') {
                leftSeg.value += rightSeg.value[0]
                rightSeg.value = rightSeg.value.slice(1)
                this._dragStartMouseX += this._rightFirstCharWidth
                dx = mouseX - this._dragStartMouseX + scrollDelta
                continue
              }
            }
            break
          }

          // Cross an adjacent chip when right text is empty
          if (rightSeg && rightSeg.type === 'text' && !rightSeg.value.length) {
            const nextChip = this.segments[ci + 2]
            if (nextChip && nextChip.type === 'chip' && dx >= this._otherChipWidth) {
              const drag = this.segments[ci]
              this.segments.splice(ci, 3, rightSeg, nextChip, drag)
              this.segments = this.normalizeSegments(this.segments)
              this._dragStartMouseX += this._otherChipWidth
              dx = mouseX - this._dragStartMouseX + scrollDelta
              continue
            }
          }

          break
        }

        // Slide left: consume characters, then cross chips
        while (dx < 0) {
          const ci = this.segments.findIndex(s => s.id === this.draggingChipId)
          if (ci === -1) return
          const leftSeg = this.segments[ci - 1]

          // Consume a character from the left text segment
          if (leftSeg && leftSeg.type === 'text' && leftSeg.value.length > 0) {
            this._recalcDragCharWidths()
            if (this._leftLastCharWidth > 0 && -dx >= this._leftLastCharWidth) {
              const rightSeg = this.segments[ci + 1]
              if (rightSeg && rightSeg.type === 'text') {
                rightSeg.value = leftSeg.value[leftSeg.value.length - 1] + rightSeg.value
                leftSeg.value = leftSeg.value.slice(0, -1)
                this._dragStartMouseX -= this._leftLastCharWidth
                dx = mouseX - this._dragStartMouseX + scrollDelta
                continue
              }
            }
            break
          }

          // Cross an adjacent chip when left text is empty
          if (leftSeg && leftSeg.type === 'text' && !leftSeg.value.length) {
            const prevChip = this.segments[ci - 2]
            if (prevChip && prevChip.type === 'chip' && -dx >= this._otherChipWidth) {
              const drag = this.segments[ci]
              this.segments.splice(ci - 2, 3, drag, leftSeg, prevChip)
              this.segments = this.normalizeSegments(this.segments)
              this._dragStartMouseX -= this._otherChipWidth
              dx = mouseX - this._dragStartMouseX + scrollDelta
              continue
            }
          }

          break
        }

        // Clamp: chip stops at edges, mouse must return to resume
        const ci = this.segments.findIndex(s => s.id === this.draggingChipId)
        if (dx > 0) {
          let hasMore = false
          for (let i = ci + 1; i < this.segments.length; i++) {
            const s = this.segments[i]
            if (s.type === 'chip' || (s.type === 'text' && s.value.length > 0)) {
              hasMore = true
              break
            }
          }
          if (!hasMore) dx = 0
        } else if (dx < 0) {
          let hasMore = false
          for (let i = ci - 1; i >= 0; i--) {
            const s = this.segments[i]
            if (s.type === 'chip' || (s.type === 'text' && s.value.length > 0)) {
              hasMore = true
              break
            }
          }
          if (!hasMore) dx = 0
        }

        this._dragChipEl.style.transform = `translateX(${dx}px)`
      },

      _checkDragAutoScroll () {
        const container = this._getScrollContainer()
        if (!container) return
        const rect = container.getBoundingClientRect()
        const margin = 40
        const mouseX = this._lastMouseX

        const distFromRight = rect.right - mouseX
        const distFromLeft = mouseX - rect.left

        if (distFromRight < margin || distFromLeft < margin) {
          if (!this._dragScrollRaf) {
            this._dragScrollRaf = requestAnimationFrame(() => this._dragAutoScroll())
          }
        }
      },

      _dragAutoScroll () {
        this._dragScrollRaf = null
        if (this.draggingChipId === null) return

        const container = this._getScrollContainer()
        if (!container) return
        const rect = container.getBoundingClientRect()
        const margin = 40
        const maxSpeed = 8
        const mouseX = this._lastMouseX

        let speed = 0
        const distFromRight = rect.right - mouseX
        const distFromLeft = mouseX - rect.left

        if (distFromRight < margin) {
          speed = maxSpeed * (1 - distFromRight / margin)
        } else if (distFromLeft < margin) {
          speed = -maxSpeed * (1 - distFromLeft / margin)
        }

        if (speed === 0) return

        container.scrollLeft += speed
        this._updateDragPosition(this._lastMouseX)

        this._dragScrollRaf = requestAnimationFrame(() => this._dragAutoScroll())
      },

      onChipLostCapture () {
        if (!this._dragChipEl) return

        if (this._dragScrollRaf) {
          cancelAnimationFrame(this._dragScrollRaf)
          this._dragScrollRaf = null
        }

        this._dragChipEl.style.transform = ''

        if (this._dragContainer) {
          this._dragContainer.removeEventListener('pointermove', this._onPointermove)
          this._dragContainer.removeEventListener('lostpointercapture', this._onLostCapture)
        }

        this.draggingChipId = null
        this._dragChipEl = null
        this._dragContainer = null
        this._dragStartMouseX = 0
        this._dragStartScrollLeft = 0
        this._lastMouseX = 0
        this._otherChipWidth = 0
        this._rightFirstCharWidth = 0
        this._leftLastCharWidth = 0
        this._onPointermove = null
        this._onLostCapture = null
        this.segments = this.normalizeSegments(this.segments)
        this.emitValue()
      },

      // --- Add / Remove ---

      addChip (chipType) {
        let targetSeg = null
        let targetIndex = -1

        if (this.lastFocusedSegId) {
          targetIndex = this.segments.findIndex(s => s.id === this.lastFocusedSegId && s.type === 'text')
          if (targetIndex !== -1) {
            targetSeg = this.segments[targetIndex]
          }
        }

        if (!targetSeg) {
          for (let i = this.segments.length - 1; i >= 0; i--) {
            if (this.segments[i].type === 'text') {
              targetSeg = this.segments[i]
              targetIndex = i
              break
            }
          }
        }

        if (!targetSeg) return

        let cursorPos = this.lastCursorPos
        const inputEl = this.getSegmentInput(targetSeg.id)
        if (inputEl) {
          cursorPos = inputEl.selectionStart || this.lastCursorPos
        }
        cursorPos = Math.min(cursorPos, targetSeg.value.length)

        const before = targetSeg.value.slice(0, cursorPos)
        const after = targetSeg.value.slice(cursorPos)

        const ct = chipType || this.resolvedChipTypes[0]
        if (!ct) return
        const newSegs = [makeTextSeg(before), makeChipSeg(ct.placeholder), makeTextSeg(after),]
        this.segments.splice(targetIndex, 1, ...newSegs)
        this.segments = this.normalizeSegments(this.segments)
        this.emitValue()

        this.$nextTick(() => {
          const afterSeg = this.segments[targetIndex + 2]
          if (afterSeg && afterSeg.type === 'text') {
            this.focusSegment(afterSeg.id, 0)
          }
        })
      },

      removeChip (chipIndex) {
        const chip = this.segments[chipIndex]
        if (!chip || chip.type !== 'chip') return

        this.segments.splice(chipIndex, 1)
        this.segments = this.normalizeSegments(this.segments)
        this.emitValue()

        this.$nextTick(() => {
          const focusSeg = this.segments[Math.min(chipIndex, this.segments.length - 1)]
          if (focusSeg && focusSeg.type === 'text') {
            this.focusSegment(focusSeg.id)
          }
        })
      },

      // --- Helpers ---

      focusSegment (segId, cursorPos) {
        const input = this.getSegmentInput(segId)
        if (input) {
          input.focus()
          if (typeof cursorPos === 'number') {
            input.setSelectionRange(cursorPos, cursorPos)
          }
          this.$nextTick(() => this._scrollInputIntoView(input))
        }
      },

      getSegmentInput (segId) {
        const refs = this.$refs['seg-' + segId]
        if (refs) return Array.isArray(refs) ? refs[0] : refs
        return null
      },

      segmentWrapStyle (seg, i) {
        if (this.segments.length === 1) return {}
        if (i === this.segments.length - 1) {
          return { minWidth: this.measureText(seg.value), }
        }
        return { width: this.measureText(seg.value), }
      },

      measureText (text) {
        if (!this.measureCanvas) return '4px'
        if (this.segments.length === 1) return '100%'

        const ctx = this.measureCanvas.getContext('2d')
        ctx.font = '15px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        const width = Math.ceil(ctx.measureText(text || '').width)
        return Math.max(width, 4) + 'px'
      },

      _getScrollContainer () {
        return this.$el ? this.$el.querySelector('.chip-input-segments') : null
      },

      getChipLabel (seg) {
        const ct = this.resolvedChipTypes.find(t => t.placeholder === seg.placeholder)
        return ct ? ct.label : seg.placeholder
      },

      _scrollInputIntoView (input) {
        const container = this._getScrollContainer()
        if (!container || !input) return
        const cRect = container.getBoundingClientRect()
        const iRect = input.getBoundingClientRect()
        if (iRect.right > cRect.right) {
          container.scrollLeft += iRect.right - cRect.right + 4
        } else if (iRect.left < cRect.left) {
          container.scrollLeft -= cRect.left - iRect.left + 4
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.chip-label-input {
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .chip-controls-row {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .info-tooltip {
    display: inline-flex;
    align-items: center;
  }

  .info-icon {
    font-size: 16px;
    color: #888;
    cursor: help;
  }
}

.chip-input-group {
  display: flex;
  align-items: center;
  border: 1px solid #d3d3d3;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  height: 40px;
  transition: border-color 0.2s;

  &.focused {
    border-color: #7957d5;
  }

  &.dragging {
    background: #fafafa;

    .segment-text-wrap:last-child {
      flex: 0 0 auto;
    }
  }

  .chip-input-segments {
    display: flex;
    align-items: center;
    flex: 1;
    height: 100%;
    min-width: 0;
    padding: 0 10px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
  }

  .segment-text-wrap {
    display: inline-flex;
    align-items: center;
    height: 100%;
    min-width: 8px;
    flex: 0 0 auto;

    &:last-child {
      flex: 1 0 auto;
    }

    &.first-segment {
      width: 100%;
    }
  }

  .segment-text {
    min-width: 4px;
    width: 100%;
    font-size: 15px;
    padding: 8px 0;
    outline: none;
    border: none;
    height: 100%;
    background: transparent;
    color: #363636;
  }

  .chip-tag {
    display: flex;
    align-items: center;
    background: #f5f5f5;
    color: #555;
    padding: 0 8px;
    font-size: 14px;
    font-weight: 500;
    height: 28px;
    border-radius: 6px;
    white-space: nowrap;
    cursor: ew-resize;
    user-select: none;
    flex-shrink: 0;
    margin: 0 2px;
    touch-action: none;

    &.no-drag {
      cursor: default;
    }

    &.is-dragging {
      z-index: 1;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    }

    .chip-drag-icon {
      font-size: 12px;
      color: #aaa;
      margin-right: 4px;
      line-height: 1;
    }

    .chip-label-text {
      margin-right: 4px;
    }

    .remove-chip-btn {
      margin-left: 2px;
      color: #888;
      font-size: 16px;
      line-height: 1;
      height: 20px;
      width: 20px;
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;

      &:hover {
        background: rgba(0, 0, 0, 0.1);
        color: #555;
      }
    }
  }
}
</style>
